import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab, Box, Typography, TextField, Alert, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress, Paper,
  List, ListItem, ListItemText, Chip, Select, MenuItem,
  FormControl, InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RichTextField from '../../editor/RichTextField';
import DiffViewer from './DiffViewer';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`implementation-tabpanel-${index}`}
      aria-labelledby={`implementation-tab-${index}`}
      {...other}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 2, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * ImplementationDialog - Shows AI-generated implementation guidance and allows refinement
 */
const ImplementationDialog = ({
  open,
  onClose,
  data,
  originalContent,
  onApplyImprovedRequirement,
  currentModel,
  availableModels,
  onModelChange,
  onRefineImplementation
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editedRequirement, setEditedRequirement] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [promptText, setPromptText] = useState('');
  const [selectedModel, setSelectedModel] = useState(currentModel || 'gpt-4o');

  // Initialize when data changes
  useEffect(() => {
    if (data?.improved_requirement) {
      setEditedRequirement(data.improved_requirement);
    } else {
      setEditedRequirement('');
    }
    
    if (data?.original_prompt) {
      setPromptText(data.original_prompt);
    } else {
      setPromptText('');
    }
    
    if (data) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [data]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleApplyRequirement = () => {
    if (onApplyImprovedRequirement && editedRequirement) {
      onApplyImprovedRequirement(editedRequirement);
    }
  };
  
  const handleSendMessage = () => {
    if (chatMessage.trim() && onRefineImplementation) {
      const newMessage = { role: 'user', content: chatMessage };
      setChatHistory([...chatHistory, newMessage]);
      
      // Send to backend for refinement
      onRefineImplementation(chatMessage, editedRequirement)
        .then(() => {
          // Add AI response to chat history
          setChatHistory(prev => [...prev, { 
            role: 'assistant',
            content: 'Implementation guidance updated based on your feedback.'
          }]);
        })
        .catch(error => {
          // Add error message to chat history
          setChatHistory(prev => [...prev, { 
            role: 'system',
            content: `Error: ${error.message || 'Failed to process refinement'}`
          }]);
        });
      
      // Clear input
      setChatMessage('');
    }
  };
  
  const handleModelChange = (event) => {
    const newModel = event.target.value;
    setSelectedModel(newModel);
    if (onModelChange) {
      onModelChange(newModel);
    }
  };

  // Generate implementation type badge color
  const getImplementationTypeColor = (type) => {
    const typeColors = {
      'software': 'primary',
      'research': 'secondary',
      'business process': 'success',
      'legal': 'warning',
      'documentation': 'info',
      'error': 'error'
    };
    
    if (!type) return 'default';
    
    // Try to match the type with our color mapping
    for (const [key, color] of Object.entries(typeColors)) {
      if (type.toLowerCase().includes(key)) {
        return color;
      }
    }
    
    return 'default'; // Default color if no match
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { height: '90vh', display: 'flex', flexDirection: 'column' } },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Implementation Guidance</Typography>
          
          {data?.implementation_type && (
            <Chip 
              label={data.implementation_type} 
              color={getImplementationTypeColor(data.implementation_type)}
              size="small"
              sx={{ ml: 1 }}
            />
          )}
          
          <Box sx={{ ml: 'auto', minWidth: 150 }}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="model-select-label">AI Model</InputLabel>
              <Select
                labelId="model-select-label"
                value={selectedModel}
                onChange={handleModelChange}
                label="AI Model"
                disabled={loading}
              >
                {availableModels?.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name || model.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Implementation Guide" />
          <Tab label="Edit Improved Requirement" />
          <Tab label="Diff View" />
          <Tab label="Refine Implementation" />
        </Tabs>
      </Box>
      
      <DialogContent dividers sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : !data ? (
          <Alert severity="error" sx={{ m: 2 }}>
            No data returned from AI service. Please try again later.
          </Alert>
        ) : (
          <>
            {/* Implementation Guide Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                {/* Original Requirement Section */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Original Requirement
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {originalContent}
                  </Typography>
                </Paper>
                
                {/* Analysis Section */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Analysis
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {data.analysis}
                  </Typography>
                </Paper>
                
                {/* Implementation Steps */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Implementation Steps
                  </Typography>
                  <List>
                    {data.implementation_steps?.map((step, index) => (
                      <ListItem key={step} divider={index < data.implementation_steps.length - 1}>
                        <ListItemText 
                          primary={`${index + 1}. ${step}`}
                          slotProps={{ primary: { component: 'div', sx: { whiteSpace: 'pre-wrap' } } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                
                {/* Considerations */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Considerations
                  </Typography>
                  <List>
                    {data.considerations?.map((item, index) => (
                      <ListItem key={item} divider={index < data.considerations.length - 1}>
                        <ListItemText 
                          primary={`• ${item}`}
                          slotProps={{ primary: { component: 'div', sx: { whiteSpace: 'pre-wrap' } } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                
                {/* Resources */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Resources
                  </Typography>
                  <List>
                    {data.resources?.map((resource, index) => (
                      <ListItem key={resource} divider={index < data.resources.length - 1}>
                        <ListItemText 
                          primary={`• ${resource}`}
                          slotProps={{ primary: { component: 'div', sx: { whiteSpace: 'pre-wrap' } } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                
                {/* Original Prompt (Collapsible) */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Original Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      fullWidth
                      multiline
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      minRows={5}
                      variant="outlined"
                      slotProps={{
                        input: {
                          readOnly: true,
                        }
                      }}
                      sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </TabPanel>
            
            {/* Edit Improved Requirement Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Edit the improved requirement:
                </Typography>
                <RichTextField
                  value={editedRequirement}
                  onChange={(content) => setEditedRequirement(content)}
                  minHeight={450}
                  tinymceApiKey="your-tinymce-api-key"
                  tinymceInit={{
                    height: 450, // Changed from '100%' to specific height and increased by ~50%
                    menubar: false,
                    statusbar: true, // Enable status bar for resize handle
                    resize: 'both', // Allow vertical and horizontal resizing
                    plugins: ['link', 'lists', 'code', 'autoresize'],
                    toolbar: 'bold italic | formatselect | bullist numlist | link code',
                    content_style: `
                      body { 
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; 
                        font-size: 14px;
                        margin: 8px;
                        padding: 4px;
                      }
                    `,
                    toolbar_location: 'top',
                    toolbar_sticky: true,
                    skin: 'oxide',
                    content_css: 'default',
                    branding: false,
                    elementpath: false,
                    autoresize_min_height: 450,
                    autoresize_max_height: 800,
                  }}
                />
              </Box>
            </TabPanel>
            
            {/* Diff View Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="subtitle2" gutterBottom>
                Compare original with improved requirement:
              </Typography>
              <DiffViewer 
                oldText={originalContent} 
                newText={editedRequirement}
              />
            </TabPanel>
            
            {/* Refine Implementation Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflow: 'auto', mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  {chatHistory.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      Ask questions or request refinements to the implementation guidance.
                    </Typography>
                  ) : (
                    chatHistory.map((msg) => (
                      <Box
                        key={`${msg.role}:${msg.content}`}
                        sx={{ 
                          p: 1.5,
                          mb: 1.5, 
                          bgcolor: msg.role === 'user' ? 'action.selected' : 'background.paper', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: msg.role === 'user' ? 'primary.light' : 'divider',
                          maxWidth: '90%',
                          ml: msg.role === 'user' ? 'auto' : 0,
                          mr: msg.role === 'user' ? 0 : 'auto',
                          color: msg.role === 'system' ? '#d32f2f' : 'inherit',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Type your message or refinement request..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    variant="outlined"
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSendMessage}
                    disabled={!chatMessage.trim()}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleApplyRequirement}
          variant="contained"
          color="primary"
          disabled={loading || !data || tabValue !== 1}
        >
          Apply Improved Requirement
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImplementationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  originalContent: PropTypes.string,
  onApplyImprovedRequirement: PropTypes.func.isRequired,
  currentModel: PropTypes.string,
  availableModels: PropTypes.array,
  onModelChange: PropTypes.func,
  onRefineImplementation: PropTypes.func
};

export default ImplementationDialog;